import * as types from './actionTypes';
import getAPIPath from '../../js/modules/api-location';
import { addNotification } from './notificationActions';

export function loadCommentsSuccess(comments) {
  return { type: types.LOAD_COMMENTS_SUCCESS, comments };
}

export function loadMoreCommentsSuccess(comments) {
  return { type: types.LOAD_MORE_COMMENTS_SUCCESS, comments };
}

export function addCommentSuccess(comment) {
  return { type: types.ADD_COMMENT_SUCCESS, comment };
}

export function deleteCommentSuccess(commentId) {
  return { type: types.DELETE_COMMENT_SUCCESS, commentId };
}

export function addCommentLikeSuccess(commentId, loggedInUserId) {
  return { type: types.ADD_COMMENT_LIKE_SUCCESS, commentId, loggedInUserId };
}

export function removeCommentLikeSuccess(commentId, loggedInUserId) {
  return { type: types.REMOVE_COMMENT_LIKE_SUCCESS, commentId, loggedInUserId };
}

export function replyToCommentSuccess(parentCommentId, reply) {
  return { type: types.REPLY_TO_COMMENT_SUCCESS, parentCommentId, reply };
}

export function deleteReplySuccess(replyId, parentCommentId) {
  return { type: types.DELETE_REPLY_SUCCESS, replyId, parentCommentId };
}

export function addReplyLikeSuccess(replyId, loggedInUserId, parentCommentId) {
  return { type: types.ADD_REPLY_LIKE_SUCCESS, replyId, loggedInUserId, parentCommentId };
}

export function removeReplyLikeSuccess(replyId, loggedInUserId, parentCommentId) {
  return { type: types.REMOVE_REPLY_LIKE_SUCCESS, replyId, loggedInUserId, parentCommentId };
}

export function reportCommentSuccess(commentId) {
  return { type: types.REPORT_COMMENT_SUCCESS, commentId };
}

export function reportReplySuccess(replyId, parentCommentId) {
  return { type: types.REPORT_REPLY_SUCCESS, replyId, parentCommentId };
}

export function loadComments(modelId, modelType, pageSize, cursor, loadMore) {
  let cursorParams = '';
  if (cursor) {
    cursorParams = `&cursor=${cursor}`;
  }

  return function(dispatch) {
    return fetch(`${getAPIPath}queryapi/comment/${modelId}?model_type=${modelType}&page_size=${pageSize}${cursorParams}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => {
      response.json().then((res) => {
        // We need to distinguish whether we are loading more comments or loading new comments (e.g. switching drills in a session)
        // The resulting reducer will either replace the entire comments object, or append new comments to the end of current comments
        if (loadMore === true) {
          dispatch(loadMoreCommentsSuccess(res.data));
        } else {
          dispatch(loadCommentsSuccess(res.data));
        }
      });
    }).catch((error) => {
      throw (error);
    });
  };
}

export function addComment(commentId, contentType, commentText) {
  const item = {};
  item.content_id = commentId;
  item.content_type = contentType;
  item.comment_content = commentText;

  return function(dispatch) {
    return fetch(`${getAPIPath}commandapi/comment`, {
      method: 'POST',
      body: JSON.stringify(item),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => {
      response.json().then((comment) => {
        dispatch(addCommentSuccess(comment.data));
      });
    }).catch((error) => {
      throw (error);
    });
  };
}

export function deleteComment(commentId, commentOrReply, parentCommentId) {
  return function(dispatch) {
    return fetch(`${getAPIPath}queryapi/comment/${commentId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => {
      response.json().then(() => {
        if (commentOrReply === 'reply') {
          dispatch(deleteReplySuccess(commentId, parentCommentId));
        } else {
          dispatch(deleteCommentSuccess(commentId));
        }
      });
    }).catch((error) => {
      throw (error);
    });
  };
}

export function addCommentLike(commentId, loggedInUserId, commentOrReply, parentCommentId) {
  const item = {};
  item.model_id = commentId;
  item.model_type = 'comment';

  return function(dispatch) {
    // Optmistic UI pattern
    if (commentOrReply === 'reply') {
      dispatch(addReplyLikeSuccess(commentId, loggedInUserId, parentCommentId));
    } else {
      dispatch(addCommentLikeSuccess(commentId, loggedInUserId));
    }

    return fetch(`${getAPIPath}commandapi/like`, {
      method: 'POST',
      body: JSON.stringify(item),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => {
      response.json().then(() => {
        if (response.status !== 200) {
          if (commentOrReply === 'reply') {
            dispatch(removeReplyLikeSuccess(commentId, loggedInUserId, parentCommentId));
          } else {
            dispatch(removeCommentLikeSuccess(commentId, loggedInUserId));
          }

          dispatch(addNotification({
            message: 'There was a problem liking that comment. Please contact us if the problem persists.',
            level: 'error',
            autoDismiss: 0,
          }));
        }
      });
    }).catch((error) => {
      throw (error);
    });
  };
}

export function removeCommentLike(commentId, loggedInUserId, commentOrReply, parentCommentId) {
  return function(dispatch) {
    // Optmistic UI pattern
    if (commentOrReply === 'reply') {
      dispatch(removeReplyLikeSuccess(commentId, loggedInUserId, parentCommentId));
    } else {
      dispatch(removeCommentLikeSuccess(commentId, loggedInUserId));
    }

    return fetch(`${getAPIPath}queryapi/like/${commentId}?model_type=comment`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => {
      response.json().then(() => {
        if (response.status !== 200) {
          if (commentOrReply === 'reply') {
            dispatch(addReplyLikeSuccess(commentId, loggedInUserId, parentCommentId));
          } else {
            dispatch(addCommentLikeSuccess(commentId, loggedInUserId));
          }

          dispatch(addNotification({
            message: 'There was a problem unliking that comment. Please contact us if the problem persists.',
            level: 'error',
            autoDismiss: 0,
          }));
        }
      });
    }).catch((error) => {
      throw (error);
    });
  };
}

export function replyToComment(commentId, replyContent) {
  const item = {};
  item.comment_id = commentId;
  item.comment_content = replyContent;

  return function(dispatch) {
    return fetch(`${getAPIPath}commandapi/reply-to-comment`, {
      method: 'POST',
      body: JSON.stringify(item),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => {
      response.json().then((reply) => {
        dispatch(replyToCommentSuccess(commentId, reply.data));
      });
    }).catch((error) => {
      throw (error);
    });
  };
}

export function reportComment(commentId, commentOrReply, parentCommentId) {
  const item = {};
  item.model_id = commentId;
  item.model_type = 'Comment';

  return function(dispatch) {
    // Optmistic UI pattern
    if (commentOrReply === 'reply') {
      dispatch(reportReplySuccess(commentId, parentCommentId));
    } else {
      dispatch(reportCommentSuccess(commentId));
    }

    return fetch(`${getAPIPath}commandapi/report`, {
      method: 'POST',
      body: JSON.stringify(item),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    .then((response) => {
      response.json().then(() => {
        if (response.status !== 200) {
          dispatch(addNotification({
            message: 'There was a problem reporting that comment. Please contact us if the problem persists.',
            level: 'error',
            autoDismiss: 0,
          }));
        }
      });
    }).catch((error) => {
      throw (error);
    });
  };
}



// WEBPACK FOOTER //
// ./library/app/actions/commentActions.js